import { useQuery } from "@tanstack/react-query";

import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { DataTable, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import {
  JOURNEY_CUTS,
  JOURNEY_KEYS,
  formatJourneyRate,
  formatJourneySeconds,
  journeyCutLine,
  journeyDay,
  journeyIsStale,
  journeyLowDataLine,
  journeyPathSearch,
  journeySearchToQuery,
  journeyUrlState,
  pickJourneyState,
} from "./journey-format";
import appJourneyManifest from "../app_journey/manifest.json";
import { JourneyStepDrawer } from "./JourneyStepDrawer";
import { FUNNEL_SLUG, RANGE_LABELS, RANGES } from "./onboarding-format";
import { formatMoney, formatOrders, formatRate } from "./stats-format";
import {
  getFunnelJourney,
  getFunnelJourneySegments,
  getFunnelOutcomes,
  statsJourneyKey,
  statsJourneySegmentsKey,
  statsOutcomesKey,
  type FunnelJourneyStats,
  type JourneyCutKey,
  type JourneyQuery,
  type JourneyStep,
  type OutcomeRow,
} from "./stats-api";

const NO_QUERY: JourneyQuery = {
  from: "",
  to: "",
  path: "pre",
  funnel: "all-pre",
  source: null,
  door: null,
  goal: null,
  device: null,
  language: null,
};

const PATH_SPEC: FilterSpec = {
  name: JOURNEY_KEYS.path,
  label: "Path",
  kind: "choice",
  options: [
    { value: "pre", label: "Pre-purchase" },
    { value: "post", label: "Post-purchase" },
  ],
};

const RANGE_SPEC: FilterSpec = {
  name: JOURNEY_KEYS.range,
  label: "Window",
  kind: "choice",
  options: RANGES.map((range) => ({ value: range, label: RANGE_LABELS[range] })),
};

const CUT_LABELS: Record<JourneyCutKey, string> = {
  source: "Source",
  goal: "Goal",
  device: "Device",
  door: "Door",
  language: "Language",
};

type FunnelOption = FunnelJourneyStats["funnels"][number];
type StepRow =
  | { kind: "step"; key: string; step: JourneyStep }
  | { kind: "button"; key: string; label: string };

const numeric = { meta: { align: "right" as const } };
const noPaging = (length: number) => ({ pageIndex: 0, pageSize: Math.max(length, 1), rowCount: length });
/** The App Journey viewer lives in its own area; read it from that module's manifest, never this tab's area. */
export const APP_JOURNEY_AREA_KEY: string = appJourneyManifest.area_key;

const PINNED_TOGGLE = "sticky left-0 z-10 bg-background in-[.bg-ks-purple-4]:bg-ks-purple-4";
const PINNED_STEP = "sticky left-14 z-10 bg-background in-[.bg-ks-purple-4]:bg-ks-purple-4";
const TABLE_FRAME = "w-full min-w-0 overflow-x-auto [&_table]:min-w-[64rem]";

function setSearchValue(search: Record<string, unknown>, key: string, value: string | null): Record<string, unknown> {
  const next = { ...search };
  if (value) next[key] = value;
  else delete next[key];
  return next;
}

function JourneyControls({
  search,
  state,
  funnels,
  segments,
  rangeError,
  onSearch,
}: {
  search: Record<string, unknown>;
  state: ReturnType<typeof journeyUrlState>;
  funnels: FunnelOption[];
  segments: Partial<Record<JourneyCutKey, Array<{ value: string; people: number }>>>;
  rangeError: string | null;
  onSearch: (next: Record<string, unknown>) => void;
}) {
  const pathValues = { [JOURNEY_KEYS.path]: state.path };
  const rangeValues = { [JOURNEY_KEYS.range]: state.range };
  const matchingFunnels = funnels.filter((funnel) => funnel.path === state.path);
  const funnelOptions = matchingFunnels.some((funnel) => funnel.key === state.funnel)
    ? matchingFunnels
    : [{ key: state.funnel, label: state.funnel, path: state.path, mapped: true }, ...matchingFunnels];
  const changePath = (next: Record<string, string | string[] | null>) => {
    const path = next[JOURNEY_KEYS.path] === "post" ? "post" : "pre";
    onSearch(journeyPathSearch(search, path));
  };
  const changeRange = (next: Record<string, string | string[] | null>) => {
    onSearch(setSearchValue(search, JOURNEY_KEYS.range, String(next[JOURNEY_KEYS.range] ?? "30")));
  };
  const hasCuts = JOURNEY_CUTS.some((key) => state[key] !== null);
  const clearCuts = () => {
    const next = { ...search };
    for (const key of JOURNEY_CUTS) delete next[key === "language" ? JOURNEY_KEYS.language : JOURNEY_KEYS[key]];
    onSearch(next);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-4">
        <FilterBar layout="segmented" filters={[PATH_SPEC]} values={pathValues} onChange={changePath} />
        <label className="flex flex-col gap-1 text-sm text-ks-ink">
          Funnel
          <Select value={state.funnel} onValueChange={(value) => onSearch(setSearchValue(search, JOURNEY_KEYS.funnel, value))}>
            <SelectTrigger data-nonmutating className="min-w-56" aria-label="Funnel">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {funnelOptions.map((funnel) => (
                <SelectItem key={funnel.key} value={funnel.key}>
                  {funnel.label}{funnel.mapped ? "" : " (needs data)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <FilterBar layout="segmented" filters={[RANGE_SPEC]} values={rangeValues} onChange={changeRange} />
      </div>

      {state.range === "custom" && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            From
            <Input data-nonmutating type="date" value={state.from ?? ""} onChange={(event) => onSearch(setSearchValue(search, JOURNEY_KEYS.from, event.target.value || null))} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            To
            <Input data-nonmutating type="date" value={state.to ?? ""} onChange={(event) => onSearch(setSearchValue(search, JOURNEY_KEYS.to, event.target.value || null))} />
          </label>
          {rangeError && <span role="alert" className="text-sm text-ks-warn">{rangeError}</span>}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3" role="search" aria-label="Journey cuts">
        {JOURNEY_CUTS.map((key) => {
          const values = segments[key] ?? [];
          const current = state[key];
          return (
            <label key={key} className="flex flex-col gap-1 text-sm text-ks-ink">
              {CUT_LABELS[key]}
              <Select
                value={current ?? "__all"}
                onValueChange={(value) => onSearch(setSearchValue(search, key === "language" ? JOURNEY_KEYS.language : JOURNEY_KEYS[key], value === "__all" ? null : value))}
              >
                <SelectTrigger data-nonmutating className="min-w-36" aria-label={CUT_LABELS[key]}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All</SelectItem>
                  {current && !values.some((option) => option.value === current) && <SelectItem value={current}>{current}</SelectItem>}
                  {values.map((option) => <SelectItem key={option.value} value={option.value}>{option.value} · {option.people}</SelectItem>)}
                  {values.length === 0 && <SelectItem disabled value="__empty">Every value in the window is listed here once there are people</SelectItem>}
                </SelectContent>
              </Select>
            </label>
          );
        })}
        {hasCuts && <Button data-nonmutating variant="outline" onClick={clearCuts}>Clear cuts</Button>}
      </div>
    </div>
  );
}

function ExperimentButton() {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex" tabIndex={0}><Button disabled size="sm">Test this step</Button></span>
        </TooltipTrigger>
        <TooltipContent>Experiments arrive in the next release</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ToggleCell({ step, open, toggle }: { step: JourneyStep; open: boolean; toggle: () => void }) {
  if (!step.records_choice) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild><span className="inline-flex size-11 items-center justify-center" data-toggle-slot>—</span></TooltipTrigger>
          <TooltipContent>This step records no choice</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return (
    <Button
      data-nonmutating
      data-toggle-slot
      variant="ghost"
      size="icon"
      className="size-11"
      aria-expanded={open}
      aria-label={`${open ? "Close" : "Open"} buttons for ${step.label ?? step.step_id}`}
      onClick={(event) => { event.stopPropagation(); toggle(); }}
    >
      {open ? <ChevronDown /> : <ChevronRight />}
    </Button>
  );
}

function stepRows(steps: JourneyStep[], open: ReadonlySet<string>): StepRow[] {
  const rows: StepRow[] = [];
  for (const step of steps) {
    rows.push({ kind: "step", key: step.step_id, step });
    if (!open.has(step.step_id)) continue;
    for (const button of step.buttons) {
      rows.push({
        kind: "button",
        key: `${step.step_id}:button:${button.button}`,
        label: `${button.button} · ${button.people} · ${formatJourneyRate(button.share_of_viewed)} of viewed`,
      });
    }
    if (step.no_button) {
      rows.push({
        kind: "button",
        key: `${step.step_id}:no-button`,
        label: `No button pressed · ${step.no_button.people} · ${formatJourneyRate(step.no_button.share_of_viewed)} of viewed`,
      });
    }
  }
  return rows;
}

function stepColumns(open: ReadonlySet<string>, toggle: (stepId: string) => void): ColumnDef<StepRow, unknown>[] {
  const figure = (row: StepRow, read: (step: JourneyStep) => number | null, format: (value: number | null) => string = String) =>
    row.kind === "step" ? format(read(row.step)) : "";
  return [
    {
      id: "toggle",
      header: "",
      cell: ({ row }) => {
        const original = row.original;
        return original.kind === "step" ? <ToggleCell step={original.step} open={open.has(original.step.step_id)} toggle={() => toggle(original.step.step_id)} /> : null;
      },
      meta: { width: "3.5rem", className: PINNED_TOGGLE },
    },
    {
      id: "step",
      header: "Step",
      accessorFn: (row) => row.kind === "step" ? (row.step.label ?? row.step.step_id) : row.label,
      cell: ({ row }) => {
        if (row.original.kind === "button") return <span className="pl-3 text-muted-foreground">↳ {row.original.label}</span>;
        const step = row.original.step;
        const label = `${step.position ?? "—"} ${step.label ?? step.step_id}`;
        return step.last_seen_at ? (
          <TooltipProvider><Tooltip><TooltipTrigger asChild><span>{label}</span></TooltipTrigger><TooltipContent>Data through {new Date(step.last_seen_at).toLocaleString("en-GB", { timeZone: "UTC" })}</TooltipContent></Tooltip></TooltipProvider>
        ) : label;
      },
      meta: { width: "19rem", className: PINNED_STEP },
    },
    { id: "viewed", header: "Viewed", accessorFn: (row) => figure(row, (step) => step.viewed, (value) => value == null ? "—" : String(value)), ...numeric },
    { id: "completed", header: "Completed", accessorFn: (row) => figure(row, (step) => step.completed, (value) => value == null ? "—" : String(value)), ...numeric },
    { id: "lost", header: "Lost", accessorFn: (row) => figure(row, (step) => step.lost, (value) => value == null ? "—" : String(value)), ...numeric },
    { id: "loss-rate", header: "Loss rate", accessorFn: (row) => figure(row, (step) => step.loss_rate, formatJourneyRate), ...numeric },
    { id: "in-progress", header: "In progress", accessorFn: (row) => figure(row, (step) => step.in_progress, (value) => value == null ? "—" : String(value)), ...numeric },
    { id: "median", header: "Median time", accessorFn: (row) => figure(row, (step) => step.median_seconds, formatJourneySeconds), ...numeric },
    { id: "experiment", header: "", cell: ({ row }) => row.original.kind === "step" ? <ExperimentButton /> : null },
  ];
}

function KpiStrip({ stats }: { stats: FunnelJourneyStats }) {
  return (
    <section aria-label="Journey KPIs" className="grid w-full min-w-0 grid-cols-2 gap-3 min-[900px]:grid-cols-4">
      {stats.kpis.map((kpi) => (
        <Card key={kpi.key} size="sm" className="min-w-0">
          <CardHeader><CardTitle>{kpi.label}</CardTitle></CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-ks-ink" title={kpi.rate == null ? "The service has no figure for this cut." : undefined}>{formatJourneyRate(kpi.rate)}</p>
            <p className="text-sm text-muted-foreground">{kpi.people ?? "—"} of {kpi.base ?? "—"}</p>
          </CardContent>
        </Card>
      ))}
    </section>
  );
}

function MilestoneBar({ stats }: { stats: FunnelJourneyStats }) {
  const first = stats.milestones[0]?.people;
  const biggestLost = Math.max(...stats.milestones.map((milestone) => milestone.lost ?? -1));
  return (
    <section className="flex w-full min-w-0 flex-col gap-2" aria-label="Journey milestones">
      <h2 className="text-base font-bold text-ks-ink">Milestones</h2>
      {stats.milestones.map((milestone, index) => {
        const width = first && milestone.people != null ? Math.max(0, Math.min(100, (milestone.people / first) * 100)) : 0;
        const biggest = milestone.lost != null && milestone.lost === biggestLost;
        return (
          <div key={milestone.key} className="grid min-w-0 grid-cols-1 gap-1 sm:grid-cols-2">
            <span className="order-1 text-sm text-ks-ink">{milestone.label} {milestone.people ?? "—"}</span>
            {index > 0 && <span className="order-3 min-w-0 text-sm text-ks-ink sm:order-2 sm:text-right">{formatJourneyRate(milestone.rate_from_previous)} of previous · {milestone.lost ?? "—"} lost</span>}
            <div className="order-2 h-3 min-w-0 overflow-hidden rounded-full bg-ks-grey-4 sm:order-3 sm:col-span-2" aria-label={`${milestone.label}: ${milestone.people ?? "—"} people`}>
              <div className={`h-full rounded-full ${biggest ? "bg-ks-purple" : "bg-ks-grey-2"}`} style={{ width: `${width}%` }} />
            </div>
          </div>
        );
      })}
    </section>
  );
}

type OutcomeDisplay = Pick<OutcomeRow, "customers" | "repeat_rate" | "orders_per_customer" | "ltv_90d_gbp" | "contribution_per_customer" | "revenue_gbp" | "suppressed">;

function AfterOrderCard({ row, pending }: { row: OutcomeDisplay | null; pending: boolean }) {
  const shown = (value: string) => row?.suppressed ? "Fewer than 5 people" : value;
  const cells = row ? [
    ["Customers", shown(formatOrders(row.customers))],
    ["Repeat rate", shown(formatRate(row.repeat_rate))],
    ["Orders per customer", shown(row.orders_per_customer == null ? "—" : row.orders_per_customer.toFixed(2))],
    ["90-day value", shown(formatMoney(row.ltv_90d_gbp))],
    ["Contribution per customer", shown(formatMoney(row.contribution_per_customer))],
    ["Revenue", shown(formatMoney(row.revenue_gbp))],
  ] : [];
  return (
    <Card>
      <CardHeader><CardTitle>After the order</CardTitle></CardHeader>
      <CardContent className="flex flex-col gap-3">
        {pending ? <Skeleton className="h-20 w-full" /> : row ? (
          <dl className="grid grid-cols-2 gap-3 lg:grid-cols-6">
            {cells.map(([label, value]) => <div key={label}><dt className="text-xs text-muted-foreground">{label}</dt><dd className="font-bold text-ks-ink">{value}</dd></div>)}
          </dl>
        ) : <p className="text-sm text-muted-foreground">After-order figures are unavailable for this window.</p>}
        <p className="text-sm text-muted-foreground">People whose first order fell in the window. Moved here from the Outcomes tab. It follows the goal cut; source and device cuts arrive with the journey model.</p>
      </CardContent>
    </Card>
  );
}

function needsDataLabel(key: string): string {
  if (key === "signins") return "Sign-ins";
  if (key === "utm") return "UTM";
  return key.replaceAll("_", " ").replace(/^./, (letter) => letter.toUpperCase());
}

function LoadingJourney() {
  return <div className="flex flex-col gap-4" aria-label="Loading journey"><div className="grid grid-cols-2 gap-3 min-[900px]:grid-cols-4">{Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className="h-28" />)}</div><Skeleton className="h-40 w-full" /><Skeleton className="h-80 w-full" /></div>;
}

export function JourneyTab({ areaName: _areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const urlState = journeyUrlState(search);
  const parsed = journeySearchToQuery(search);
  const rangeError = "error" in parsed ? parsed.error : null;
  const lastGood = useRef<JourneyQuery | null>(null);
  if ("query" in parsed) lastGood.current = parsed.query;
  const query = lastGood.current ?? NO_QUERY;
  const fresh = useRef(false);
  const go = (next: Record<string, unknown>) => void navigate({ to: ".", search: next, replace: true });

  const journey = useQuery({
    queryKey: statsJourneyKey(FUNNEL_SLUG, query),
    queryFn: () => getFunnelJourney(FUNNEL_SLUG, query, fresh.current),
    enabled: query !== NO_QUERY,
  });
  const segments = useQuery({
    queryKey: statsJourneySegmentsKey(FUNNEL_SLUG, query),
    queryFn: () => getFunnelJourneySegments(FUNNEL_SLUG, query),
    enabled: query !== NO_QUERY,
  });
  const stats = journey.data?.data;
  const [funnels, setFunnels] = useState<FunnelOption[]>([]);
  const [open, setOpen] = useState<Set<string>>(new Set());
  useEffect(() => { if (stats) setFunnels(stats.funnels); }, [stats]);
  useEffect(() => {
    if (!stats?.biggest_loss || !stats.steps.some((step) => step.step_id === stats.biggest_loss?.step_id && step.records_choice)) return;
    setOpen((previous) => previous.has(stats.biggest_loss!.step_id) ? previous : new Set([...previous, stats.biggest_loss!.step_id]));
  }, [stats]);

  const toggle = (stepId: string) => setOpen((previous) => {
    const next = new Set(previous);
    if (next.has(stepId)) next.delete(stepId); else next.add(stepId);
    return next;
  });
  const rows = stepRows(stats?.steps ?? [], open);
  const state = pickJourneyState(stats, { pending: journey.isPending, error: journey.isError });
  const cohort: 30 | 90 = parsed.range === "30" ? 30 : 90;
  const outcomes = useQuery({
    queryKey: statsOutcomesKey(FUNNEL_SLUG, "goal", cohort),
    queryFn: () => getFunnelOutcomes(FUNNEL_SLUG, "goal", cohort),
  });
  const outcomeStats = outcomes.data?.data;
  const outcomeRow: OutcomeDisplay | null = outcomeStats
    ? query.goal
      ? outcomeStats.rows.find((row) => row.goal === query.goal) ?? null
      : { ...outcomeStats.totals, suppressed: false }
    : null;
  const cutLine = stats ? journeyCutLine(stats.cut) : null;
  const closeDrawer = () => go(setSearchValue(search, JOURNEY_KEYS.step, null));
  const setBy = (by: JourneyCutKey) => go(setSearchValue(search, JOURNEY_KEYS.by, by));
  const reload = async () => {
    fresh.current = true;
    try { await journey.refetch(); } finally { fresh.current = false; }
  };

  return (
    <div className="flex w-full min-w-0 flex-col gap-5">
      <JourneyControls
        search={search}
        state={urlState}
        funnels={funnels}
        segments={segments.data?.data.segments ?? {}}
        rangeError={rangeError}
        onSearch={go}
      />

      {segments.isError && (
        <Alert role="alert">
          <AlertTitle>Filter values unavailable.</AlertTitle>
          <AlertDescription>
            The cut lists could not be loaded, so each filter shows All only. Nothing was changed.{" "}
            <Button data-nonmutating variant="link" className="px-0" onClick={() => void segments.refetch()}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {rangeError && lastGood.current && (
        <p className="text-sm text-ks-warn">
          The custom range is not valid yet, so the figures below are still for {lastGood.current.from} to {lastGood.current.to}.
        </p>
      )}

      {state === "error" ? (
        <Alert role="alert">
          <AlertTitle>Journey figures unavailable.</AlertTitle>
          <AlertDescription>The funnel stats service did not answer. Nothing was changed. Try again shortly. <Button data-nonmutating variant="link" className="px-0" onClick={() => void journey.refetch()}>Try again</Button></AlertDescription>
        </Alert>
      ) : state === "loading" ? <LoadingJourney /> : stats ? (
        <>
          {cutLine && <p className="text-sm text-ks-ink">{cutLine}</p>}
          {stats.low_data && <p className="text-sm text-ks-warn">{journeyLowDataLine(stats.people)}</p>}
          {stats.data_as_of && journeyIsStale(stats.data_as_of) && <p className="text-sm text-ks-warn">Figures are from {journeyDay(stats.data_as_of)}. The daily copy has not landed; the Data Integrity alert has fired.</p>}

          {state === "unmapped" ? (
            <p className="text-sm text-ks-ink">{stats.funnel.label} has no door or source tag in the app yet, so it has no figures.</p>
          ) : state === "empty" ? (
            <p className="text-sm text-ks-ink">No people in this cut yet. <Button data-nonmutating variant="link" className="px-0" onClick={() => {
              const next = { ...search };
              for (const key of JOURNEY_CUTS) delete next[key === "language" ? JOURNEY_KEYS.language : JOURNEY_KEYS[key]];
              go(next);
            }}>Clear cuts</Button></p>
          ) : (
            <>
              <KpiStrip stats={stats} />
              <MilestoneBar stats={stats} />
              <section className="flex w-full min-w-0 flex-col gap-2">
                <h2 className="text-base font-bold text-ks-ink">Steps</h2>
                <div className={TABLE_FRAME}>
                  <DataTable
                    columns={stepColumns(open, toggle)}
                    rows={rows}
                    page={noPaging(rows.length)}
                    onPageChange={() => {}}
                    density="compact"
                    rowClassName={(row) => row.kind === "step" && row.step.step_id === stats.biggest_loss?.step_id ? "bg-ks-purple-4" : undefined}
                    onRowClick={(row) => row.kind === "step" && go(setSearchValue(search, JOURNEY_KEYS.step, row.step.step_id))}
                    emptyMessage="No journey steps to show."
                    caption="Journey steps, losses, time and buttons pressed"
                  />
                </div>
                <p className="text-sm text-muted-foreground">The arrow opens the buttons people pressed on a step, and how many pressed none. People, not taps. One person who pressed two buttons counts under their last press.</p>
              </section>
            </>
          )}

          <Card>
            <CardHeader><CardTitle>Needs data</CardTitle></CardHeader>
            <CardContent className="flex flex-col gap-1">
              {stats.needs_data.map((item) => <p key={item.key} className="text-sm text-ks-ink">{needsDataLabel(item.key)}: {item.reason}</p>)}
            </CardContent>
          </Card>
          <AfterOrderCard row={outcomeRow} pending={outcomes.isPending} />
          <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>Figures through {stats.data_as_of ? new Date(stats.data_as_of).toLocaleString("en-GB", { timeZone: "UTC" }) : "—"}. Every count is the real number, however small.</span>
            <Button data-nonmutating variant="link" className="px-0" onClick={() => void reload()}>Reload</Button>
          </p>
        </>
      ) : null}

      <JourneyStepDrawer
        slug={FUNNEL_SLUG}
        query={query}
        stepId={urlState.step}
        by={urlState.by}
        areaKey={APP_JOURNEY_AREA_KEY}
        onClose={closeDrawer}
        onByChange={setBy}
      />
    </div>
  );
}
